/**
 * Tenant capabilities — frontend mirror of the backend resolver
 * (echo-crm-backend: src/common/tenancy/tenant-capabilities.ts).
 *
 *   A CAPABILITY decides whether functionality is available.
 *   INDUSTRY (tenant.industry / `vertical`) may only decide copy: labels,
 *   placeholders, examples and suggested defaults.
 *
 * The backend resolves the EFFECTIVE capabilities and sends them in
 * `GET /auth/me` → `user.tenant.capabilities`. The fallback below is only used
 * when that payload is missing (older backend / cached user); it mirrors the
 * resolver: persisted key wins → legacy alias → industry preset.
 *
 * Pure module (no React, no api imports) so it can be unit-tested under node.
 */

export type KitchenFlow = "PAY_FIRST" | "PAY_LATER";

export interface TenantCapabilities {
  inventory: boolean;
  crm: boolean;
  cashControlRequired: boolean;
  allowNegativeStock: boolean;
  /** Lots/FEFO consumption. Persisted key: batchInventory. */
  useBatchInventory: boolean;
  /** Sell by weight/decimals + presentations. Persisted key: uomEnabled. */
  weightAndPresentations: boolean;
  /** Business CAN track units individually (IMEI or serial number). */
  serializedInventoryEnabled: boolean;
  /** Cash/card/financing prices. */
  enableMultiplePriceTypes: boolean;
  enableSalespersonSelector: boolean;
  /** Effective: already ANDed with the selector. */
  requireSalespersonOnSale: boolean;
  restaurantEnabled: boolean;
  /** Effective only with restaurant mode on. */
  tables: boolean;
  /** Effective only with restaurant mode on. */
  kitchen: boolean;
  kitchenFlow: KitchenFlow;
  salesSummaryEmailAddon: boolean;
  shopifyIntegration: boolean;
}

export type BooleanCapabilityKey = Exclude<keyof TenantCapabilities, "kitchenFlow">;

/** Persisted `Tenant.features` keys that are canonical capabilities. */
type PersistedCapabilityKey =
  | "inventory"
  | "crm"
  | "cashControlRequired"
  | "allowNegativeStock"
  | "batchInventory"
  | "uomEnabled"
  | "serializedInventoryEnabled"
  | "enableMultiplePriceTypes"
  | "enableSalespersonSelector"
  | "requireSalespersonOnSale"
  | "restaurantEnabled"
  | "tables"
  | "kitchen";

type CapabilityPreset = Record<PersistedCapabilityKey, boolean> & {
  kitchenFlow?: KitchenFlow;
};

const BASE_PRESET: CapabilityPreset = {
  inventory: true,
  crm: true,
  cashControlRequired: true,
  allowNegativeStock: true,
  batchInventory: false,
  uomEnabled: false,
  serializedInventoryEnabled: false,
  enableMultiplePriceTypes: false,
  enableSalespersonSelector: false,
  requireSalespersonOnSale: false,
  restaurantEnabled: false,
  tables: false,
  kitchen: false,
};

/** Recommended per-industry deltas (backend is the source of truth). */
const INDUSTRY_PRESETS: Record<string, Partial<CapabilityPreset>> = {
  GROCERY: { allowNegativeStock: false, batchInventory: true, uomEnabled: true },
  RESTAURANT: { restaurantEnabled: true, tables: true, kitchen: true, kitchenFlow: "PAY_FIRST" },
  PHONES: {
    allowNegativeStock: false,
    serializedInventoryEnabled: true,
    enableMultiplePriceTypes: true,
    enableSalespersonSelector: true,
    requireSalespersonOnSale: false,
  },
};

function getPreset(industry: string | null | undefined): CapabilityPreset {
  const delta = industry ? INDUSTRY_PRESETS[industry] : undefined;
  return { ...BASE_PRESET, ...(delta ?? {}) };
}

/**
 * Suggested default for "Permitir stock negativo" when creating a tenant
 * (wizard default only — the admin's choice is what gets persisted).
 */
export function getDefaultAllowNegativeStock(industry: string | null | undefined): boolean {
  return getPreset(industry).allowNegativeStock;
}

type FeatureRecord = Record<string, unknown>;

const asRecord = (v: unknown): FeatureRecord =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as FeatureRecord) : {};
const isDefined = (f: FeatureRecord, k: string) => f[k] !== undefined && f[k] !== null;

function persistedOrPreset(f: FeatureRecord, key: PersistedCapabilityKey, preset: CapabilityPreset) {
  return isDefined(f, key) ? f[key] === true : preset[key];
}

/**
 * Fallback resolver used when the backend payload has no `capabilities`.
 * Mirrors resolveTenantCapabilities() in the backend.
 */
export function resolveCapabilitiesFallback(
  industry: string | null | undefined,
  features: unknown,
): TenantCapabilities {
  const f = asRecord(features);
  const preset = getPreset(industry);

  // Legacy alias: only an explicit `batchesEnabled: true` counts.
  const useBatchInventory = isDefined(f, "batchInventory")
    ? f.batchInventory === true
    : f.batchesEnabled === true || preset.batchInventory;

  const restaurantEnabled = persistedOrPreset(f, "restaurantEnabled", preset);
  const enableSalespersonSelector = persistedOrPreset(f, "enableSalespersonSelector", preset);
  const kitchenFlow: KitchenFlow =
    f.kitchenFlow === "PAY_LATER" || f.kitchenFlow === "PAY_FIRST"
      ? f.kitchenFlow
      : (preset.kitchenFlow ?? "PAY_FIRST");

  return {
    inventory: persistedOrPreset(f, "inventory", preset),
    crm: persistedOrPreset(f, "crm", preset),
    cashControlRequired: persistedOrPreset(f, "cashControlRequired", preset),
    allowNegativeStock: persistedOrPreset(f, "allowNegativeStock", preset),
    useBatchInventory,
    weightAndPresentations: persistedOrPreset(f, "uomEnabled", preset),
    serializedInventoryEnabled: persistedOrPreset(f, "serializedInventoryEnabled", preset),
    enableMultiplePriceTypes: persistedOrPreset(f, "enableMultiplePriceTypes", preset),
    enableSalespersonSelector,
    requireSalespersonOnSale:
      enableSalespersonSelector && persistedOrPreset(f, "requireSalespersonOnSale", preset),
    restaurantEnabled,
    tables: restaurantEnabled && persistedOrPreset(f, "tables", preset),
    kitchen: restaurantEnabled && persistedOrPreset(f, "kitchen", preset),
    kitchenFlow,
    salesSummaryEmailAddon: f.salesSummaryEmailAddon === true,
    shopifyIntegration: f.shopifyIntegration === true,
  };
}

const BOOLEAN_CAPABILITY_KEYS: BooleanCapabilityKey[] = [
  "inventory",
  "crm",
  "cashControlRequired",
  "allowNegativeStock",
  "useBatchInventory",
  "weightAndPresentations",
  "serializedInventoryEnabled",
  "enableMultiplePriceTypes",
  "enableSalespersonSelector",
  "requireSalespersonOnSale",
  "restaurantEnabled",
  "tables",
  "kitchen",
  "salesSummaryEmailAddon",
  "shopifyIntegration",
];

/**
 * Effective capabilities for a tenant: the backend `capabilities` payload when
 * present (per key, so a partial payload still works), else the fallback.
 */
export function resolveTenantCapabilities(
  tenant:
    | { industry?: string | null; features?: unknown; capabilities?: Partial<TenantCapabilities> | null }
    | null
    | undefined,
): TenantCapabilities {
  const fallback = resolveCapabilitiesFallback(tenant?.industry, tenant?.features);
  const raw = tenant?.capabilities;
  if (!raw || typeof raw !== "object") return fallback;

  const result: TenantCapabilities = { ...fallback };
  for (const key of BOOLEAN_CAPABILITY_KEYS) {
    if (typeof raw[key] === "boolean") result[key] = raw[key] as boolean;
  }
  if (raw.kitchenFlow === "PAY_FIRST" || raw.kitchenFlow === "PAY_LATER") {
    result.kitchenFlow = raw.kitchenFlow;
  }
  return result;
}

// ─── Human labels (SaaS Admin / toasts) ──────────────────────────────────────

/** Human labels for persisted capability keys (Tenant.features). */
export const CAPABILITY_FEATURE_LABELS: Record<string, string> = {
  inventory: "Inventario",
  crm: "Clientes / CRM",
  batchInventory: "Lotes y vencimientos (FEFO)",
  uomEnabled: "Venta por peso y presentaciones",
  serializedInventoryEnabled: "Inventario serializado",
  allowNegativeStock: "Vender sin existencia",
  enableMultiplePriceTypes: "Precios por método de pago",
  enableSalespersonSelector: "Selector de vendedor en POS",
  requireSalespersonOnSale: "Vendedor obligatorio",
  restaurantEnabled: "Modo restaurante",
  tables: "Mesas",
  kitchen: "Cocina / KDS",
  cashControlRequired: "Exigir caja abierta para vender",
  salesSummaryEmailAddon: "Resumen de ventas por email (add-on)",
  shopifyIntegration: "Integración Shopify (add-on, beta)",
};

/** Short helper copy shown next to a capability toggle (SaaS Admin). */
export const CAPABILITY_FEATURE_HINTS: Record<string, string> = {
  batchInventory: "Con inventario histórico, cambiarlo requiere una transición administrativa.",
  serializedInventoryEnabled:
    "Controla unidades individualmente mediante IMEI o número de serie.",
};

/**
 * Legacy / dead / placeholder keys that may still exist in `Tenant.features`.
 * Hidden from the SaaS Admin toggles (never deleted from the data).
 */
export const HIDDEN_LEGACY_FEATURE_KEYS: ReadonlySet<string> = new Set([
  "batchesEnabled",
  "batches",
  "lotsEnabled",
  "expiryTrackingEnabled",
  "colorSize",
  "creditEnabled",
  "priceTiersEnabled",
  "enableWholesalePricing",
  "quickService",
  "recipes",
  "recipesEnabled",
  "services",
  "appointments",
  "loyaltyEnabled",
  "enableCardProcessorSelector",
  "requireCardProcessorOnCardPayment",
  "enableTransferDestinationAccountSelector",
  "requireTransferDestinationAccount",
  "enablePricePaymentConsistencyChecks",
]);

/**
 * The CONFIGURED value of a persisted capability key, as the SaaS Admin should
 * see it: persisted value when present, else (legacy alias →) industry preset.
 * Not ANDed with parents (e.g. `tables` without restaurant) — this is what the
 * switch writes, the backend normalizes dependencies on save.
 */
export function getConfiguredFeatureValue(
  industry: string | null | undefined,
  features: unknown,
  key: string,
): boolean {
  const f = asRecord(features);
  if (isDefined(f, key)) return f[key] === true;
  const preset = getPreset(industry);
  if (key === "batchInventory") return f.batchesEnabled === true || preset.batchInventory;
  if (key in preset) return preset[key as PersistedCapabilityKey] === true;
  return false;
}

/**
 * Keys a MANAGER cannot change (backend drops them silently for non-owners).
 * Mirrors PosController.OWNER_ONLY_CAPABILITY_KEYS.
 */
export const OWNER_ONLY_CAPABILITY_KEYS = [
  "cashControlRequired",
  "allowNegativeStock",
  "serializedInventoryEnabled",
  "enableMultiplePriceTypes",
  "enableSalespersonSelector",
  "requireSalespersonOnSale",
] as const;

export const OWNER_ONLY_HINT = "Solo el propietario puede cambiar esta configuración.";

// ─── Backend capability errors / auto changes ────────────────────────────────

export type CapabilityErrorCode = "CAPABILITY_REQUIRES_TRANSITION" | "CAPABILITY_DEPENDENCY_BLOCKED";

export interface CapabilityError {
  code: CapabilityErrorCode;
  capability?: string;
  message: string;
}

/** Extracts `{ code, capability, message }` from a capability 400, else null. */
export function getCapabilityError(error: unknown): CapabilityError | null {
  const e = error as any;
  const candidates = [e?.response?.data, e?.data, e];
  for (const body of candidates) {
    if (!body || typeof body !== "object") continue;
    const code = body.code;
    if (
      (code === "CAPABILITY_REQUIRES_TRANSITION" || code === "CAPABILITY_DEPENDENCY_BLOCKED") &&
      typeof body.message === "string" &&
      body.message.trim()
    ) {
      return {
        code,
        capability: typeof body.capability === "string" ? body.capability : undefined,
        message: body.message.trim(),
      };
    }
  }
  return null;
}

/**
 * Turns a backend auto-change entry ("restaurantEnabled=true (requerido por
 * Mesas/Cocina)") into readable copy. Unknown formats are returned as-is.
 */
export function describeAutoChange(entry: string): string {
  const match = /^\s*(\w+)\s*=\s*(true|false)\s*(?:\((.*)\))?\s*$/.exec(entry);
  if (!match) return entry;
  const [, key, value, reason] = match;
  const label = CAPABILITY_FEATURE_LABELS[key] ?? key;
  const state = value === "true" ? "activado" : "desactivado";
  return reason ? `${label}: ${state} (${reason})` : `${label}: ${state}`;
}

/** Reads `autoChanges` from an update response, humanized. */
export function getAutoChanges(response: unknown): string[] {
  const list = (response as any)?.autoChanges;
  if (!Array.isArray(list)) return [];
  return list.filter((s): s is string => typeof s === "string" && !!s.trim()).map(describeAutoChange);
}
